import { useCallback, useRef, useState } from "react";
import {
  ActionIcon, Button, Group, Menu, Stack, TextInput, Title,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { IconDots, IconPencil, IconPlus, IconTrash } from "@tabler/icons-react";
import { DataTable, DataTableColumn } from "@/components/DataTable";
import { FormModal } from "@/components/FormModal";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Can } from "@/components/Can";
import { api } from "@/lib/api";
import { formatAPIError, qk } from "@/lib/apiHelpers";
import type {
  Company, CompanyCreateRequest, CompanyUpdateRequest, CompaniesListResponse,
} from "@/types/api";
import { CompanyForm, CompanyFormApi } from "./CompanyForm";

export default function CompaniesPage() {
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [debounced] = useDebouncedValue(search, 300);

  const filters = { page, page_size: 50, q: debounced };
  const { data, isLoading } = useQuery({
    queryKey: qk.companies(filters),
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set("page", String(filters.page));
      params.set("page_size", String(filters.page_size));
      if (filters.q) params.set("q", filters.q);
      return (await api.get<CompaniesListResponse>(`/api/v1/companies?${params.toString()}`)).data;
    },
  });

  const [editing, setEditing] = useState<Company | null | undefined>(undefined);
  // formApi через useRef — см. длинный коммент в Drivers/index.tsx (Fix #8).
  // Mantine useForm возвращает новый объект на каждый рендер, useState захватывает
  // только первый (с initial values), и submit видит пустую форму.
  const formApiRef = useRef<CompanyFormApi | null>(null);
  const setFormApi = useCallback((api: CompanyFormApi) => {
    formApiRef.current = api;
  }, []);
  const [confirmDelete, setConfirmDelete] = useState<Company | null>(null);

  const createMut = useMutation({
    mutationFn: async (body: CompanyCreateRequest) =>
      (await api.post<Company>("/api/v1/companies", body)).data,
    onSuccess: () => {
      notifications.show({ message: "Фирма создана", color: "green" });
      void qc.invalidateQueries({ queryKey: ["companies"] });
      setEditing(undefined);
    },
    onError: (e) => notifications.show({ title: "Ошибка", message: formatAPIError(e), color: "red" }),
  });
  const updateMut = useMutation({
    mutationFn: async ({ id, body }: { id: string; body: CompanyUpdateRequest }) =>
      (await api.patch<Company>(`/api/v1/companies/${id}`, body)).data,
    onSuccess: () => {
      notifications.show({ message: "Фирма обновлена", color: "green" });
      void qc.invalidateQueries({ queryKey: ["companies"] });
      setEditing(undefined);
    },
    onError: (e) => notifications.show({ title: "Ошибка", message: formatAPIError(e), color: "red" }),
  });
  const deleteMut = useMutation({
    mutationFn: async (id: string) => (await api.delete(`/api/v1/companies/${id}`)).data,
    onSuccess: () => {
      notifications.show({ message: "Фирма удалена", color: "green" });
      void qc.invalidateQueries({ queryKey: ["companies"] });
      setConfirmDelete(null);
    },
    onError: (e) => notifications.show({ title: "Ошибка", message: formatAPIError(e), color: "red" }),
  });

  const submit = () => {
    const formApi = formApiRef.current;
    if (!formApi) return;
    if (formApi.validate().hasErrors) return;
    const v = formApi.values;
    if (editing) {
      // PATCH: ОБЯЗАТЕЛЬНО шлём пустые строки явно (а не undefined), чтобы
      // бэк через NULLIF реально занулил поле. Если undefined — поле не
      // попадёт в PATCH и старое значение сохранится (баг #66/#87).
      const body: CompanyUpdateRequest = {
        code: v.code,
        name: v.name,
        full_name: v.full_name,
        inn: v.inn,
        kpp: v.kpp,
        ogrn: v.ogrn,
        address: v.address,
        bank_account: v.bank_account,
        bank_bik: v.bank_bik,
        bank_name: v.bank_name,
        vat_default: v.vat_default,
        signatory_name: v.signatory_name,
        signatory_position: v.signatory_position,
      };
      updateMut.mutate({ id: editing.id, body });
    } else {
      const body: CompanyCreateRequest = {
        code: v.code, name: v.name, full_name: v.full_name, inn: v.inn,
        kpp: v.kpp || undefined, ogrn: v.ogrn || undefined,
        address: v.address || undefined,
        bank_account: v.bank_account || undefined,
        bank_bik: v.bank_bik || undefined,
        bank_name: v.bank_name || undefined,
        vat_default: v.vat_default,
        signatory_name: v.signatory_name || undefined,
        signatory_position: v.signatory_position || undefined,
      };
      createMut.mutate(body);
    }
  };

  const columns: DataTableColumn<Company>[] = [
    { key: "code", header: "Код", render: (c) => c.code, width: 120 },
    { key: "name", header: "Название", render: (c) => c.name },
    { key: "inn", header: "ИНН", render: (c) => c.inn, width: 140 },
    { key: "vat", header: "НДС", render: (c) => `${c.vat_default}%`, width: 80 },
    {
      key: "actions",
      header: "",
      width: 60,
      render: (c) => (
        <Can perm="companies:write">
          <Menu position="bottom-end">
            <Menu.Target><ActionIcon variant="subtle" onClick={(e) => e.stopPropagation()}><IconDots size={16} /></ActionIcon></Menu.Target>
            <Menu.Dropdown>
              <Menu.Item leftSection={<IconPencil size={14} />} onClick={() => setEditing(c)}>Редактировать</Menu.Item>
              <Menu.Item leftSection={<IconTrash size={14} />} color="red" onClick={() => setConfirmDelete(c)}>Удалить</Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Can>
      ),
    },
  ];

  return (
    <Stack gap="lg">
      <Group justify="space-between">
        <Title order={2}>Свои фирмы</Title>
        <Can perm="companies:write">
          <Button leftSection={<IconPlus size={16} />} onClick={() => setEditing(null)}>Создать</Button>
        </Can>
      </Group>

      <TextInput
        placeholder="Поиск по названию, ИНН, коду..."
        value={search}
        onChange={(e) => { setSearch(e.currentTarget.value); setPage(1); }}
        style={{ maxWidth: 400 }}
      />

      <DataTable
        columns={columns}
        rows={data?.items}
        loading={isLoading}
        total={data?.total ?? 0}
        page={page}
        pageSize={50}
        onPageChange={setPage}
        rowKey={(c) => c.id}
        onRowClick={(c) => setEditing(c)}
      />

      <FormModal
        opened={editing !== undefined}
        onClose={() => setEditing(undefined)}
        title={editing ? "Редактировать фирму" : "Новая фирма"}
        onSubmit={submit}
        loading={createMut.isPending || updateMut.isPending}
        size="lg"
      >
        <CompanyForm initial={editing ?? undefined} onChange={setFormApi} />
      </FormModal>

      <ConfirmDialog
        opened={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={() => confirmDelete && deleteMut.mutate(confirmDelete.id)}
        title="Удалить фирму?"
        message={`Фирма "${confirmDelete?.name}" будет удалена. Действие необратимо.`}
        loading={deleteMut.isPending}
      />
    </Stack>
  );
}
